import { useQuery } from '@apollo/client';
import { CircularProgress } from '@mui/material';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { FC, useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { ConnectedProps, connect } from 'react-redux';
import { useNavigate } from 'react-router';

import { gengql } from '@/__generated__';
import { ConsultationState, EngagementType } from '@/__generated__/graphql';
import { addRegistrant, getJoinInfo } from '@/actions/call';
import { inviteUser } from '@/actions/invitation';
import { track } from '@/actions/tracking';
import { hideMessage, notify } from '@/actions/ui';
import InaccessibleArchived from '@/components/InaccessibleArchived/InaccessibleArchived';
import LayoutPage from '@/components/Layout/LayoutPage';
import MediaQuery from '@/components/MediaQuery';
import ProjectBar from '@/components/ProjectBar';
import { JoinInfo } from '@/conference';
import {
  ALREADY_CANCELED,
  ALREADY_CONFIRMED,
  ALREADY_STARTED,
  NOT_ENOUGH_CREDITS,
  OUTDATED_ERROR,
  Trigger,
  deleteConsultation,
  dismissConsultationReview,
  orderTranscript,
  updateConsultation,
} from '@/consultation/store';
import { ERROR_CODES, apiErrorMessage, hasErrorCode } from '@/core/api';
import { Viewer } from '@/core/viewer';
import RequestDetails from '@/expertrequest/components/ExpertRequestForm/DetailsNew';
import { useReportMissingProject } from '@/expertrequest/reportMissingProject';
import { useApp, usePermissions } from '@/hooks/useAppContext';
import NotFoundPage from '@/pages/NotFoundPage';
import { RootState } from '@/store';
import { SCREEN_SM } from '@/theme/screens';
import { formatDateTime } from '@/utils';
import { DocumentNodeResult } from '@/utils/gql';
import { RateDisplayType, getRateDisplayTypeForLoggedIn } from '@/utils/rateDisplay';

import s from './Consultation.module.scss';
import Dialogs from './Dialogs';
import Header from './Header/Header';
import JoinCall from './JoinCall';
import Transcript from './Transcript';
import Metrics from './metrics/Metrics';
import CallDetails from './sections/CallDetails';
import ExpertRequestDetails from './sections/ExpertRequestDetails';
import FeedbackDetails from './sections/FeedbackDetails';
import WrittenResponse from './sections/WrittenResponse';

const userNotAvailable = '(User not available)';

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

function getExpertName(c: Consultation) {
  return c.expert?.name || c.expert_name || userNotAvailable;
}

function getUserName(viewer: Viewer, consultation: Consultation) {
  const { expert } = consultation;
  const isViewerExpert = viewer.id === expert?.id;
  return isViewerExpert ? consultation.requester_name : getExpertName(consultation);
}

function userAbleToSelectTime(viewer: Viewer, consultation: Consultation) {
  const { expert, requester, state } = consultation;
  return (
    state === 'negotiating_expert_time' &&
    ((expert?.id === viewer.id && !!expert?.compliance_completed_at) || requester?.id === viewer.id)
  );
}

export type Section =
  | 'call_details'
  | 'response_details'
  | 'request'
  | 'transcript'
  | 'feedback'
  | 'metrics'
  | undefined;

interface ConsultationProps {
  id: string;
  hire?: boolean;
  section?: Section;
  initialUsefulnessRating: number | undefined;
  initialMatchExperienceRating: number | undefined;
  openCompleteTraining?: boolean;
  openReviewCall?: boolean;
  openRequestTranscript?: boolean;
  timeChosen?: any;
}

const connector = connect(
  (state: RootState) => {
    return {
      viewer: state.viewer,
      call: state.call,
      userContext: state.ui.userContext,
      reviewsDismissedPermanently: state.consultationReviews.dismissedPermanently,
    };
  },
  {
    addRegistrant,
    deleteConsultation,
    dismissConsultationReview,
    hideMessage,
    inviteUser,
    getJoinInfo,
    notify,
    orderTranscript,
    updateConsultation,
    track,
  }
);

const GET_CONSULTATION = gengql(/* GraphQL */ `
  query getConsultation($id: String!) {
    consultation(id: $id) {
      id
      html_url
      type
      description
      starts_at
      proposed_times
      rejected_times
      expected_duration
      initial_expected_duration
      billing_duration
      started_at
      ended_at
      created_at
      credit_rate
      disclosure
      summary
      costs {
        id
        state
        charge_type
        credits {
          cents
          currency
        }
        money {
          cents
          currency
        }
      }
      usd_charge_status
      usd_charge_amount {
        cents
        currency
      }
      bill_rate
      expert_payout
      estimated_expert_payout
      completion_adds_expert_to_network
      engagement_type
      state
      archived
      expert_identifier
      requester_identifier
      cancel_reason
      external
      recording_url
      recording_duration
      original_recording_url
      tracking_code
      conference {
        id
        carrier
        phone_numbers {
          display
          number
          country_code
        }
        duration
        registrants {
          email
          name
          identifier
          invited_by
        }
        attachments {
          file_type
          location
          recording_type
        }
        recording_state
      }
      requester {
        id
        username
        first_name
        last_name
        name
        html_url
        picture_url
        timezone
        phone
      }
      requester_timezone
      requester_name
      expert {
        id
        username
        first_name
        last_name
        name
        html_url
        picture_url
        timezone
        phone
        expert_state
        compliance_completed_at
        bill_rate
        profile {
          id
          client_rate
        }
      }
      expert_timezone
      expert_name
      expert_request {
        id
        er_type
        html_url
        name
        description
        group_about
        phone
        focus_areas
        qualifications {
          id
          query
          response_type
          required
        }
        questions {
          id
          query
          response_type
          required
        }
        attachments {
          id
          created_at
          author {
            id
            name
            html_url
          }
          expert_request_id
          consultation_id
          name
          description
          file_url
        }
        companies_pursue
        companies_avoid
        disclosure
        regions {
          id
          name
        }
        sectors {
          id
          name
        }
        project {
          id
          name
          html_url
          tracking_code
          group {
            id
            name
            # ENG-5715: the api's answer to whether this account has the
            # engagements product, replacing the bundled allowlist. Sits beside
            # allow_hire_long_term, the legacy program's own gate, because the
            # completed-consultation header offers whichever of the two applies.
            engagements_enabled
            billing_account {
              id
              allow_hire_long_term
            }
          }
          # ENG-5727: whether THIS viewer may open an engagement on this
          # project, answered by the api. Null means they may. Read this rather
          # than deriving it here: the old staff exclusion in Header.tsx was a
          # rule the api did not hold, and it routed a legitimate refusal into
          # the Typeform interest form.
          viewer_engagement_create_denial
        }
        unpaid
        instructions_research
        job_scope
        opportunity_location
        tags
        expected_duration
      }
      group {
        id
        name
        account_type
        # ENG-6301: the account's answer when the call has no project to ask.
        engagements_enabled
      }
      client_review {
        id
        match_experience_rating
        communication_rating
        connection_quality_rating
        review
        target_feedback
        onfrontiers_feedback
      }
      expert_review {
        id
        uselfulness_rating
        communication_rating
        connection_quality_rating
        review
        target_feedback
        onfrontiers_feedback
        expert_usefulness_rating
        time_savings_feedback
        improved_bid_confidence_feedback
        partner_identification_feedback
        cost_savings_feedback
        new_opportunity_identification_feedback
        other_feedback
        other_text_feedback
      }
      event {
        description
        summary
        location
        start
        end
      }
      transcription_order {
        id
        state
      }
      transcription_price {
        cents
        currency
      }
      transcription_price_usd {
        cents
        currency
      }
      transcription {
        speakers {
          id
          name
          user {
            id
            first_name
            last_name
            picture_url
            html_url
          }
        }
        monologues {
          id
          speaker {
            id
            name
            user {
              id
              first_name
              last_name
              picture_url
              html_url
            }
          }
          elements {
            type
            value
            position
          }
        }
        summary
      }
      permissions
      attachments {
        id
        created_at
        author {
          id
          name
          html_url
        }
        expert_request_id
        consultation_id
        name
        description
        file_url
      }
    }
  }
`);

export type Consultation = NonNullable<DocumentNodeResult<typeof GET_CONSULTATION>['consultation']>;

const ConsultationPage: FC<ConsultationProps & ConnectedProps<typeof connector>> = ({
  viewer,
  id,
  hire = false,
  getJoinInfo,
  hideMessage,
  notify,
  call,
  orderTranscript,
  updateConsultation,
  deleteConsultation,
  userContext,
  section: initialSection,
  inviteUser,
  addRegistrant,
  dismissConsultationReview,
  initialUsefulnessRating,
  initialMatchExperienceRating,
  openCompleteTraining: initialOpenCompleteTraining = false,
  openReviewCall: initialOpenReviewCall = false,
  openRequestTranscript: initialOpenRequestTranscript = false,
  timeChosen,
  track,
  reviewsDismissedPermanently,
}) => {
  const navigate = useNavigate();
  const { store } = useApp();

  const [canViewArchived] = usePermissions(
    [{ service: 'consultation', action: 'view_archived', resource: id }],
    [id]
  );

  const { loading, error, data, refetch } = useQuery(GET_CONSULTATION, { variables: { id } });
  const consultation = data?.consultation;

  useEffect(() => {
    if (consultation?.archived && !canViewArchived) {
      track('promo.show.consultation.archived');
    }
  }, [canViewArchived, consultation?.archived, track]);

  const section =
    initialSection ||
    (consultation?.engagement_type === EngagementType.WrittenResponse
      ? 'response_details'
      : 'call_details');

  const {
    expert_request: expertRequest,
    requester,
    expert,
    state,
    client_review: clientReview,
    expert_review: expertReview,
    transcription,
  } = consultation || {};

  // Staff only, like the project bar below, which crashed on this before ENG-5787 and now
  // hides. The api folds a denied `project:view` into null, so for other viewers a null
  // project is an ordinary permission answer, not a defect.
  useReportMissingProject('consultation_page', viewer.admin ? expertRequest : undefined);

  const hasCompletedComplianceTraining = expert && !!expert.compliance_completed_at;

  const isViewerExpert = viewer.id === expert?.id;
  const isViewerClient = viewer.id === requester?.id;
  const hasReview = !!(isViewerExpert
    ? clientReview && clientReview.id
    : expertReview && expertReview.id);
  const shouldReview =
    !consultation?.external &&
    consultation?.state === 'completed' &&
    !hasReview &&
    (isViewerClient || isViewerExpert);

  // Auto-pop the review modal only for recent, undismissed calls — matching the global
  // review pop's 30-day window (FindUnreviewedConsultations) so we don't retroactively
  // nag on old consultations. The "Leave a review" button + the ?review_call=true deep
  // link stay available at any age (ENG-5255).
  const endedWithinReviewWindow =
    !!consultation?.ended_at &&
    new Date(consultation.ended_at).getTime() > Date.now() - THIRTY_DAYS_MS;

  const autoOpenReview =
    shouldReview &&
    endedWithinReviewWindow &&
    !reviewsDismissedPermanently.some((d: any) => d === id);

  const initialSuggestTime =
    timeChosen === 'none' && !!consultation && userAbleToSelectTime(viewer, consultation);

  const [openDenyDialog, setOpenDenyDialog] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [openSuggestTime, setOpenSuggestTime] = useState(initialSuggestTime);
  const [openCompleteTraining, setOpenCompleteTraining] = useState(
    initialOpenCompleteTraining && isViewerExpert && !hasCompletedComplianceTraining
  );
  const [openReviewCall, setOpenReviewCall] = useState(initialOpenReviewCall || autoOpenReview);
  const [openRequestTranscript, setOpenRequestTranscript] = useState(initialOpenRequestTranscript);
  const [openInviteParticipant, setOpenInviteParticipant] = useState(false);
  const [date, setDate] = useState(timeChosen);
  const [submittingTranscript, setSubmittingTranscript] = useState(false);
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);
  // Join info belongs to one conference, and confirming a rescheduled call can replace it
  // (ENG-4970), so the join info and its timeout are stored with the conference id they
  // belong to. No render pairs a new conference with the old one's join info.
  const [fetchedJoinInfo, setFetchedJoinInfo] = useState<{
    conferenceId: string;
    info: JoinInfo;
  }>();
  const [joinInfoTimedOutFor, setJoinInfoTimedOutFor] = useState<string>();

  const conference = consultation?.conference;
  const joinInfo =
    fetchedJoinInfo?.conferenceId === conference?.id ? fetchedJoinInfo?.info : undefined;
  const joinInfoTimedOut = !!conference?.id && joinInfoTimedOutFor === conference.id;

  const handleUpdateJoinInfo = useCallback(async () => {
    if (conference?.carrier === 'zoom' && conference?.id) {
      try {
        setFetchedJoinInfo({ conferenceId: conference.id, info: await getJoinInfo(conference.id) });
      } catch (e: any) {
        if (
          hasErrorCode(e, ERROR_CODES.CONFERENCE_HOST_CONFERENCE_NOT_FOUND) ||
          hasErrorCode(e, ERROR_CODES.PERMISSION_NOT_ALLOWED)
        ) {
          console.warn(e);
          return;
        }
        throw e;
      }
    }
  }, [conference?.carrier, conference?.id, getJoinInfo]);

  // Poll for joinInfo when it's missing for a Zoom conference (Zoom API propagation delay)
  useEffect(() => {
    const conferenceId = conference?.id;
    if (conference?.carrier !== 'zoom' || !conferenceId || joinInfo) return;

    const interval = setInterval(handleUpdateJoinInfo, 3000);
    const timeout = setTimeout(() => {
      clearInterval(interval);
      setJoinInfoTimedOutFor(conferenceId);
    }, 30000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [conference?.carrier, conference?.id, joinInfo, handleUpdateJoinInfo]);

  const showCompleteTraining = useCallback(
    (emptyDate: any) => {
      const dateSelected = date || consultation?.proposed_times[0];

      setOpenCompleteTraining(true);
      setDate(emptyDate ? undefined : dateSelected);
    },
    [consultation, date]
  );

  const handleConfirm = useCallback(() => {
    setOpenConfirmDialog(false);
    hideMessage();

    if (!date) {
      return notify('You need to select a suggested time.', 'error');
    }

    setConfirmSubmitting(true);
    const userName = consultation && getUserName(viewer, consultation);

    updateConsultation({
      id: consultation?.id,
      starts_at: date,
      state: 'confirmed',
      trigger: Trigger.consultationPage,
    }).then(
      () => {
        setConfirmSubmitting(false);
        notify(
          `You accepted ${userName}'s suggested call time of ${formatDateTime(
            date,
            viewer.timezone || undefined
          )}.`,
          'success'
        );
        handleUpdateJoinInfo();
        // The api gives the call a new conference when its old meeting already ended
        // (ENG-4970); the mutation doesn't return it, so re-read the consultation.
        refetch().catch(() => {
          notify(
            'The call is confirmed, but its details could not be reloaded. Please refresh the page.',
            'warning'
          );
        });
      },
      (err: any) => {
        setConfirmSubmitting(false);
        if (err.message === OUTDATED_ERROR) {
          notify('Cannot schedule the event for a past date.', 'error');
        } else if (err.message === ALREADY_CONFIRMED) {
          notify('Consultation already confirmed.', 'error');
        } else if (err.message === ALREADY_CANCELED) {
          notify("This consultation is already closed and can't be changed.", 'error');
        } else {
          notify(
            apiErrorMessage(err, 'An error occurred when accepting the consultation.'),
            'error'
          );
        }
      }
    );
  }, [
    consultation,
    viewer,
    date,
    hideMessage,
    notify,
    handleUpdateJoinInfo,
    refetch,
    updateConsultation,
  ]);

  const handleAccept = useCallback(() => {
    const hasCompletedComplianceTraining = expert && !!expert.compliance_completed_at;

    if (isViewerExpert) {
      if (!hasCompletedComplianceTraining) {
        setOpenCompleteTraining(true);
        return;
      }
    }

    return handleConfirm();
  }, [expert, isViewerExpert, handleConfirm]);

  const handleDeny = useCallback(
    (values: any) => {
      hideMessage();

      setOpenDenyDialog(false);
      setDate(null);

      updateConsultation({
        id: consultation?.id,
        state: 'denied',
        cancel_reason: values.cancel_reason || '',
        trigger: Trigger.consultationPage,
      }).catch((err: unknown) => {
        notify(apiErrorMessage(err, 'An error occurred when canceling consultation.'), 'error');
      });
    },
    [consultation, hideMessage, notify, updateConsultation]
  );

  const handleDelete = useCallback(() => {
    deleteConsultation(consultation?.id)
      .then(() => {
        navigate('/consultations');
      })
      .catch((err: any) => {
        notify('Error when deleting consultation', 'error');
        throw err;
      });
  }, [consultation?.id, deleteConsultation, navigate, notify]);

  const handleCancel = useCallback(
    (values: any) => {
      hideMessage();

      setOpenCancelDialog(false);
      setDate(null);

      updateConsultation({
        id: consultation?.id,
        state: 'canceled',
        cancel_reason: values.cancel_reason || '',
        trigger: Trigger.consultationPage,
      }).catch((err: any) => {
        const message =
          err && err.message === ALREADY_STARTED
            ? 'Cannot cancel ongoing consultation.'
            : apiErrorMessage(err, 'An error occurred when canceling consultation.');
        notify(message, 'error');
      });
    },
    [consultation, hideMessage, notify, updateConsultation]
  );

  const handleInviteParticipant = useCallback(
    async (values: any) => {
      setOpenInviteParticipant(false);

      const conferenceId = conference?.id;
      if (!conferenceId) {
        notify('Error inviting participant. No conference', 'error');
        return;
      }

      try {
        await addRegistrant(consultation, {
          conferenceId,
          name: values.name,
          email: values.email,
          invited_by: viewer.name,
        });

        if (values.inviteJoinTeam) {
          const groupId =
            consultation?.group?.id || consultation?.expert_request?.project?.group?.id;
          if (!groupId) {
            notify('Invited participant but error inviting to group', 'error');
            return;
          }
          await inviteUser({
            email: values.email,
            collectionType: 'group',
            collectionId: groupId,
            role: 'member',
          });
        }
        notify('Successfully invited participant');
      } catch (ex) {
        notify('Error inviting participant', 'error');
        throw ex;
      }
    },
    [conference?.id, notify, addRegistrant, consultation, viewer.name, inviteUser]
  );

  const handleSuggestTime = useCallback(
    (values: any) => {
      if (!values || !values.dates) return;

      setOpenSuggestTime(false);
      setDate(null);

      const userName = consultation && getUserName(viewer, consultation);

      updateConsultation({
        id: consultation?.id,
        proposed_times: values.dates.filter(Boolean),
        duration: values.duration,
        state: isViewerExpert ? 'negotiating_client_time' : 'negotiating_expert_time',
        trigger: Trigger.consultationPage,
      })
        .then(() => {
          notify(`Sent time suggestion to ${userName}.`, 'success');
        })
        .catch((err: any) => {
          const message =
            err && err.message === ALREADY_STARTED
              ? 'Cannot reschedule ongoing consultation.'
              : err && err.message === NOT_ENOUGH_CREDITS
                ? 'Not enough credits to reschedule the consultation.'
                : apiErrorMessage(err, 'An error occurred when rescheduling consultation.');
          notify(message, 'error');
        });
    },
    [viewer, consultation, updateConsultation, isViewerExpert, notify]
  );

  // ENG-5504 F7: `isUSD` follows the consultation's OWN billing group (the account
  // that is/was charged) for fee display and the transcript purchase (also billed to
  // this account). The previous `?? groups[0]` fallback mis-priced consultations whose
  // billing group was not the viewer's first group. When the account is not resolvable
  // (admin/non-member viewing, or a null group) default to credits, not USD.
  const consultationGroup = viewer?.groups?.find((g) => g.id === consultation?.group?.id);
  const isUSD = consultationGroup?.billing_account
    ? getRateDisplayTypeForLoggedIn(consultationGroup.billing_account) === RateDisplayType.USD
    : false;

  const handleRequestTranscript = useCallback(() => {
    hideMessage();
    const {
      id,
      transcription_price: transcriptionPrice,
      transcription_price_usd: transcriptionPriceUSD,
    } = consultation || {};
    const price = isUSD ? (transcriptionPriceUSD ?? transcriptionPrice) : transcriptionPrice;

    setSubmittingTranscript(true);

    orderTranscript(id, price)
      .then(() => {
        notify('Transcript requested.', 'success');
        setOpenRequestTranscript(false);
        setSubmittingTranscript(false);
      })
      .catch((e: any) => {
        setSubmittingTranscript(false);

        if (e.message === 'GraphQL Error: maximum amount of credits not provided') {
          notify(
            'This consultation has an invalid duration and ' +
              'cannot be transcribed at this time. ' +
              'A member of the engineering team has been notified.',
            'error'
          );
          return;
        }

        if (e.message === 'GraphQL Error: not enough credits to order transcription') {
          notify('Not enough credits to order transcript', 'error');
          return;
        }

        notify('An error occurred when requesting the transcript.', 'error');
      });
  }, [consultation, hideMessage, isUSD, notify, orderTranscript]);

  const handleOpenSuggestTime = useCallback(() => {
    const { expert } = consultation || {};
    const hasCompletedComplianceTraining = expert && !!expert.compliance_completed_at;

    if (isViewerExpert && !hasCompletedComplianceTraining) {
      setOpenCompleteTraining(true);
    } else {
      setOpenSuggestTime(true);
    }
  }, [consultation, isViewerExpert]);

  useEffect(() => {
    if (openSuggestTime) handleOpenSuggestTime();
    handleUpdateJoinInfo();

    async function fetchRequests() {
      // @ts-expect-error fetch does not exist on RequestDetails
      await Promise.all(RequestDetails.fetch.map((f: any) => store.dispatch(f({ viewer }))));
    }
    fetchRequests();
  }, [openSuggestTime, handleUpdateJoinInfo, handleOpenSuggestTime, store, viewer]);

  const handleOpenTranscript = () => {
    setOpenRequestTranscript(true);
  };

  const dismissReview = () => {
    dismissConsultationReview(consultation?.id, true);
    setOpenReviewCall(false);
  };

  const closeDialogs = () => {
    setOpenDenyDialog(false);
    setOpenCompleteTraining(false);
    setOpenConfirmDialog(false);
    setOpenCancelDialog(false);
    setOpenSuggestTime(false);
    setOpenReviewCall(false);
    setOpenRequestTranscript(false);
    setOpenInviteParticipant(false);
  };

  // After a review is submitted, the thunk updates Redux but not the Apollo
  // GET_CONSULTATION cache this page reads from — so refetch to flip hasReview,
  // which hides the "Leave a review" button and populates the Feedback tab (ENG-5255).
  const handleReviewSubmitted = () => {
    closeDialogs();
    refetch();
  };

  if (hasErrorCode(error, ERROR_CODES.CONSULTATION_NOT_FOUND)) {
    return <NotFoundPage />;
  }

  if (loading || !consultation) {
    return (
      <MediaQuery maxWidth={SCREEN_SM}>
        {(isMobileVersion: boolean) => {
          return (
            <LayoutPage
              showNav
              selected="consultations"
              footerStyle={{
                paddingBottom: isMobileVersion ? 110 : 0,
              }}
              showReviewConsultation={!openReviewCall}
            >
              <Helmet>
                <title>Consultation</title>
              </Helmet>
              {loading && (
                <div className={s.loading}>
                  <CircularProgress />
                </div>
              )}
              {error && <span>Error loading consultation.</span>}
            </LayoutPage>
          );
        }}
      </MediaQuery>
    );
  }

  const requesterName = consultation.requester_name;
  const expertName = getExpertName(consultation);

  const isExpertActive = expert?.expert_state === 'active';
  const isViewerRequester = viewer.id === requester?.id;
  const isViewerAdmin = !!viewer.admin && !isViewerExpert && !isViewerRequester;
  const user = isViewerExpert ? requester : expert;

  const isExpired = state === 'expired';
  const isCompleted = state === 'completed';
  const isConfirmed = state === 'confirmed';
  const isCanceled = state === 'canceled';
  const isDenied = state === 'denied';
  const isIncomplete = state === 'incomplete';

  const isWaitingExpertConfirmation = state === 'negotiating_expert_time';
  const isWaitingRequesterConfirmation = state === 'negotiating_client_time';
  const isWaitingConfirmation = isWaitingExpertConfirmation || isWaitingRequesterConfirmation;
  const isWaitingViewerConfirmation =
    (isViewerExpert && isWaitingExpertConfirmation) ||
    (!isViewerExpert && isWaitingRequesterConfirmation);

  const isWrittenConsultation = consultation?.engagement_type === EngagementType.WrittenResponse;
  const showDetails = isConfirmed || isCompleted || isCanceled || isIncomplete;
  const canEdit = isViewerRequester || isViewerExpert || isViewerAdmin;
  const canConfirm = isExpertActive && isWaitingViewerConfirmation && canEdit;
  const canCancel =
    isExpertActive && (isConfirmed || (!isViewerExpert && isWaitingConfirmation)) && canEdit;
  const canDeny = isExpertActive && isViewerExpert && isWaitingConfirmation;
  // `expired` is terminal (ConsultationLifecycle.tla) — transitioning out of it fails on the
  // API, so we must not offer reschedule. Expired retries go through "Request Again" instead,
  // which starts a new consultation linked to the original ER (ENG-5304).
  const canReschedule = isExpertActive && (isConfirmed || isIncomplete);
  const canHireExpert =
    consultation?.expert_request?.project?.group?.billing_account?.allow_hire_long_term;

  const baseProps = {
    consultation,
    expertRequest,
    requesterName: requesterName || userNotAvailable,
    expertName,
    isViewerExpert,
    isExpertActive,
    isViewerRequester,
    isViewerAdmin,
    user,
    isExpired,
    isCompleted,
    isConfirmed,
    isDenied,
    isIncomplete,
    isWaitingConfirmation,
    isWaitingExpertConfirmation,
    isWaitingRequesterConfirmation,
    isWaitingViewerConfirmation,
    isWrittenConsultation,
    canEdit,
    canConfirm,
    canCancel,
    canDeny,
    canReschedule,
    canHireExpert,
    shouldReview,
    hasReview,
    isUSD,
  };

  const showRequest = !!expertRequest;
  const showTranscription =
    !isWrittenConsultation &&
    !isViewerExpert &&
    transcription &&
    transcription.monologues &&
    transcription.monologues.length > 0;
  const showFeedback = hasReview;

  const showMetrics =
    !isWrittenConsultation &&
    conference?.carrier &&
    viewer.admin &&
    state &&
    [
      ConsultationState.Confirmed,
      ConsultationState.InProgress,
      ConsultationState.Finalizing,
      ConsultationState.Completed,
      ConsultationState.Incomplete,
    ].includes(state);
  const showTabs = showRequest || showTranscription || showFeedback || showMetrics;

  if (
    section === 'metrics' &&
    (!viewer.admin ||
      !conference ||
      !['confirmed', 'in_progress', 'finalizing', 'completed', 'incomplete'].includes(
        consultation.state
      ))
  ) {
    return <NotFoundPage />;
  }

  if (consultation?.archived && !canViewArchived) {
    return (
      <>
        <Helmet>
          <title>Consultation Details</title>
        </Helmet>
        <InaccessibleArchived selectedTab="consultations" entity="consultation" />
      </>
    );
  }

  return (
    <MediaQuery maxWidth={SCREEN_SM}>
      {(isMobileVersion: boolean) => {
        // Keyed by conference so JoinCall's own join link (iOS Safari) starts over when
        // confirming replaces the conference (ENG-4970).
        const CallActions = !isWrittenConsultation && (
          <JoinCall
            key={conference?.id}
            consultation={consultation}
            joinInfo={joinInfo}
            joinInfoTimedOut={joinInfoTimedOut}
          />
        );

        return (
          <LayoutPage
            showNav
            selected="consultations"
            footerStyle={{
              paddingBottom: isMobileVersion && CallActions ? 110 : 0,
            }}
            showReviewConsultation={!openReviewCall}
          >
            <Helmet>
              <title>{`Consultation with ${expert ? expert.name : 'Expert'}`}</title>
            </Helmet>

            {/* The ER's project is nullable, and this bar dereferences it — narrow on the
                project itself, not just on the ER (Sentry WEBAPP-CDV / CDW / CDK). */}
            {viewer.admin && expertRequest?.project && (
              <ProjectBar viewer={viewer} project={expertRequest.project} />
            )}

            <Header
              {...baseProps}
              canHireExpert={true}
              hire={hire}
              expertId={expert?.id || ''}
              isMobileVersion={isMobileVersion}
              callActions={CallActions}
              onReviewCall={() => setOpenReviewCall(true)}
              onDelete={consultation.external ? handleDelete : undefined}
              shouldReview={shouldReview}
            />

            <div style={{ height: 24 }} />

            {showTabs && (
              <Tabs value={section} style={{ marginBottom: 24 }}>
                <Tab
                  onClick={() => navigate(`/consultation/${id}`)}
                  value={isWrittenConsultation ? 'response_details' : 'call_details'}
                  label={isWrittenConsultation ? 'Response Details' : 'Call Details'}
                />
                {showRequest && (
                  <Tab
                    onClick={() => navigate(`/consultation/${id}/request`)}
                    value="request"
                    label="Request Details"
                  />
                )}
                {showTranscription && (
                  <Tab
                    onClick={() => navigate(`/consultation/${id}/transcript`)}
                    value="transcript"
                    label="Transcript"
                  />
                )}
                {showFeedback && (
                  <Tab
                    onClick={() => navigate(`/consultation/${id}/feedback`)}
                    value="feedback"
                    label="Feedback"
                  />
                )}
                {showMetrics && (
                  <Tab
                    onClick={() => navigate(`/consultation/${id}/metrics`)}
                    value="metrics"
                    label="Metrics"
                  />
                )}
              </Tabs>
            )}

            <div className={s.consultationCard}>
              {section === 'call_details' && (
                <CallDetails
                  {...baseProps}
                  viewer={viewer}
                  userContext={userContext}
                  joinInfo={joinInfo}
                  showDetails={showDetails}
                  isMobileVersion={isMobileVersion}
                  ongoingCall={call.connected}
                  openReviewCall={openReviewCall}
                  onRequestTranscript={handleOpenTranscript}
                  selectedDate={date}
                  onOpenSuggestTime={handleOpenSuggestTime}
                  onDateSelect={setDate}
                  onConfirm={handleAccept}
                  onCancel={() => setOpenCancelDialog(true)}
                  onDeny={() => setOpenDenyDialog(true)}
                  onReschedule={() => setOpenSuggestTime(true)}
                  onInviteParticipant={() => setOpenInviteParticipant(true)}
                  transcription={transcription}
                  confirmSubmitting={confirmSubmitting}
                />
              )}
              {section === 'response_details' && (
                <WrittenResponse
                  {...baseProps}
                  viewer={viewer}
                  isMobileVersion={isMobileVersion}
                  showCompleteTraining={showCompleteTraining}
                />
              )}
              {section === 'request' && (
                <ExpertRequestDetails
                  consultation={consultation}
                  isViewerExpert={isViewerExpert}
                  isMobileVersion={isMobileVersion}
                />
              )}
              {!isViewerExpert && section === 'transcript' && (
                <Transcript transcription={transcription} expertId={expert?.id} />
              )}
              {showFeedback && section === 'feedback' && (
                <FeedbackDetails
                  clientReview={clientReview}
                  expertReview={expertReview}
                  isViewerExpert={isViewerExpert}
                  isViewerRequester={isViewerRequester}
                  requester={requester}
                  expert={expert}
                />
              )}
              {showMetrics && section === 'metrics' && <Metrics consultationId={id} />}
            </div>

            {isMobileVersion && CallActions && (
              <div className={s.stickyCallActions}>{CallActions}</div>
            )}

            <Dialogs
              user={user}
              consultation={consultation}
              date={date}
              viewer={viewer}
              isViewerExpert={isViewerExpert}
              isUSD={isUSD}
              onDismissReview={dismissReview}
              onCloseDialog={closeDialogs}
              onReviewSubmitted={handleReviewSubmitted}
              onDeny={handleDeny}
              onCancel={handleCancel}
              onConfirm={handleConfirm}
              onSuggestTime={handleSuggestTime}
              onRequestTranscript={handleRequestTranscript}
              onInviteParticipant={handleInviteParticipant}
              openDenyDialog={openDenyDialog}
              openConfirmDialog={openConfirmDialog}
              openCancelDialog={openCancelDialog}
              openCompleteTraining={openCompleteTraining}
              openReviewCall={openReviewCall}
              openSuggestTime={openSuggestTime}
              openRequestTranscript={openRequestTranscript}
              openInviteParticipant={openInviteParticipant}
              submittingTranscript={submittingTranscript}
              initialUsefulnessRating={initialUsefulnessRating}
              initialMatchExperienceRating={initialMatchExperienceRating}
              shouldReview={shouldReview}
              isWrittenConsultation={isWrittenConsultation}
            />
          </LayoutPage>
        );
      }}
    </MediaQuery>
  );
};

export default connector(ConsultationPage);
